"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import type { Member } from "@/data/types";

// ============================================
// Command Palette — search overlay
// ============================================

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  members: Record<string, Member>;
  onSelectMember: (memberId: string) => void;
}

export default function CommandPalette({
  isOpen,
  onClose,
  members,
  onSelectMember,
}: CommandPaletteProps) {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when opened; clear query when closed
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      // Small delay to let the CSS transition begin, then focus
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Compute results
  const allMembers = Object.values(members);
  const results = query
    ? allMembers.filter((m) => {
        const q = query.toLowerCase();
        return (
          m.name.toLowerCase().includes(q) ||
          (m.company && m.company.toLowerCase().includes(q)) ||
          (m.location && m.location.toLowerCase().includes(q)) ||
          (m.industry && m.industry.toLowerCase().includes(q)) ||
          (m.tagline && m.tagline.toLowerCase().includes(q))
        );
      })
    : allMembers.slice(0, 6);

  const handleSelect = useCallback(
    (id: string) => {
      onSelectMember(id);
      onClose();
    },
    [onSelectMember, onClose],
  );

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  return (
    <div
      className={`command-overlay${isOpen ? " open" : ""}`}
      onClick={handleBackdropClick}
    >
      <div className="command-box">
        <div className="command-input-wrap">
          <input
            ref={inputRef}
            type="text"
            className="command-input"
            placeholder="Search members by name, industry, location..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="command-results">
          {results.length > 0 ? (
            results.map((m) => (
              <div
                key={m.id}
                className="command-result-item"
                onClick={() => handleSelect(m.id)}
              >
                <div className="cr-avatar">{m.initials}</div>
                <div className="cr-info">
                  <div className="cr-name">{m.name}</div>
                  <div className="cr-detail">
                    {m.company || ""}{" "}
                    {m.location ? `\u2014 ${m.location}` : ""}{" "}
                    {m.revenue ? `\u2014 ${m.revenue}` : ""}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div
              style={{
                padding: 20,
                textAlign: "center",
                color: "var(--cream-faint)",
                fontSize: "0.85rem",
              }}
            >
              No members found
            </div>
          )}
        </div>
        <div className="command-hint">
          Press{" "}
          <kbd
            style={{
              padding: "1px 4px",
              border: "1px solid var(--cream-whisper)",
              borderRadius: 3,
              fontSize: "0.7rem",
            }}
          >
            Esc
          </kbd>{" "}
          to close &mdash; Start typing to search 1,200 members
        </div>
      </div>
    </div>
  );
}
